import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Building2, Languages, Phone, Pencil, Star, Globe, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { UserAvatar, VerifiedTick, PlanBadge } from "@/components/UserAvatar";
import { StartChatButton } from "@/components/StartChatButton";
import { Button } from "@/components/ui/button";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { professionOf } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function FollowButton({ u, setU }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toggle = async () => {
    if (!user) return navigate("/login");
    try {
      await (u.is_following ? api.delete(`/users/${u.id}/follow`) : api.post(`/users/${u.id}/follow`));
      setU({ ...u, is_following: !u.is_following, followers_count: (u.followers_count || 0) + (u.is_following ? -1 : 1) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Button onClick={toggle} variant={u.is_following ? "outline" : "default"} className={`rounded-full ${u.is_following ? "" : "bg-tm-green hover:bg-tm-green/90"}`} data-testid="profile-follow-button">
      {u.is_following ? <><UserCheck className="mr-1.5 h-4 w-4" />Following</> : <><UserPlus className="mr-1.5 h-4 w-4" />Follow</>}
    </Button>
  );
}

export function ProfileHeader({ u, setU, isMe }) {
  const phone = !u.privacy?.hide_phone && (u.whatsapp || u.contact_number);
  const facts = [
    u.city && [MapPin, u.city], (u.firm_name || u.designation) && [Building2, [u.designation, u.firm_name].filter(Boolean).join(", ")],
    u.languages?.length && [Languages, u.languages.join(", ")], u.rating && [Star, `${u.rating} rating (${u.reviews_count || 0} reviews)`],
  ].filter(Boolean);
  return (
    <div className="tm-card overflow-hidden">
      <div className="h-32 bg-tm-green tm-noise sm:h-40">{u.banner && <img src={mediaUrl(u.banner)} alt="" className="h-full w-full object-cover" />}</div>
      <div className="px-6 pb-6 sm:px-8">
        <div className="-mt-14 flex flex-wrap items-end justify-between gap-4">
          <UserAvatar user={u} size="xl" className="ring-4 ring-white" />
          <div className="flex flex-wrap gap-2">
            {isMe ? <Button asChild variant="outline" className="rounded-full" data-testid="profile-edit-button"><Link to="/settings"><Pencil className="mr-1.5 h-4 w-4" />Edit profile</Link></Button> : (
              <>
                <FollowButton u={u} setU={setU} />
                <StartChatButton userId={u.id} testid="profile-message-button" />
                {phone && <Button asChild variant="outline" className="rounded-full" data-testid="profile-whatsapp-button"><a href={`https://wa.me/91${phone}`} target="_blank" rel="noreferrer"><Phone className="mr-1.5 h-4 w-4" />WhatsApp</a></Button>}
              </>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <h1 className="font-display text-2xl font-extrabold text-tm-green sm:text-3xl" data-testid="profile-name">{u.full_name}</h1>
          <VerifiedTick user={u} /><PlanBadge user={u} />
        </div>
        <p className="mt-1 text-slate-600" data-testid="profile-headline">{u.headline || professionOf(u)}</p>
        <span className="mt-3 inline-block rounded-full bg-tm-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-tm-green" data-testid="profile-role">{professionOf(u)}</span>
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
          {facts.map(([Icon, text]) => <span key={text} className="flex items-center gap-1.5"><Icon className="h-4 w-4 text-tm-gold" />{text}</span>)}
          {u.website && <a href={u.website.startsWith("http") ? u.website : `https://${u.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-tm-green-light hover:underline"><Globe className="h-4 w-4" />{u.website}</a>}
        </div>
        <div className="mt-5 flex gap-6 text-sm">
          {[["posts_count", "Posts"], ["followers_count", "Followers"], ["following_count", "Following"]].map(([k, l]) => <span key={k}><b className="text-tm-green">{u[k] ?? 0}</b> <span className="text-slate-500">{l}</span></span>)}
        </div>
      </div>
    </div>
  );
}
